// admin/src/lib/search.ts
//
// Pure, framework-free token search + ranking for the exercises/equipment
// lists. The library loads once (a few hundred rows) and is filtered
// client-side, so this module never touches the network -- it only
// normalizes text and scores rows against a query.
//
// Rules:
//   - case-insensitive, Hebrew + English
//   - punctuation/hyphens are ignored ("pullup" finds "Pull-up")
//   - token-based AND: every query token must match somewhere (any order)
//   - typo-tolerant: tokens >= 4 chars also match with edit distance <= 1
//   - ranked: name-prefix hit > name-word hit > other-field hit

const PUNCT_RE = /[-_/.,'"()]+/g;

/** Lowercases, turns punctuation/hyphens into spaces, collapses whitespace. */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(PUNCT_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Splits normalized text into non-empty tokens. */
export function tokenize(text: string): string[] {
  const n = normalize(text);
  return n.length ? n.split(' ') : [];
}

/** Levenshtein edit distance. Fine at this scale (short tokens, few hundred rows). */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > 1) return Math.max(m, n); // can never land within tolerance 1
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

/** Whether query token `needle` matches haystack token `hay`: exact, substring, or (needle >= 4 chars) edit distance <= 1. */
function tokenMatches(needle: string, hay: string): boolean {
  if (!needle || !hay) return false;
  if (hay.includes(needle)) return true;
  if (needle.length >= 4 && editDistance(needle, hay) <= 1) return true;
  return false;
}

/** Score buckets -- name-prefix beats a name-word match, which beats any other field. */
const SCORE_NAME_PREFIX = 100;
const SCORE_NAME_WORD = 50;
const SCORE_OTHER_FIELD = 10;

export type Searchable<T> = {
  item: T;
  /** Primary fields (en/he name) -- these drive prefix/word scoring. */
  names: string[];
  /** Secondary fields (muscles, equipment, sport, ...) -- lower-weight matches. */
  extra: string[];
};

export type MatchResult<T> = { item: T; score: number };

/**
 * Token-based AND search over `rows`. Every token in `query` must match at
 * least one token from a row's names+extra (any order) for the row to be
 * kept. Kept rows are scored (sum of each query token's best match) and
 * sorted highest-first. An empty/blank query matches and scores everything
 * at 0, preserving input order.
 */
export function searchRank<T>(query: string, rows: readonly Searchable<T>[]): MatchResult<T>[] {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) {
    return rows.map((r) => ({ item: r.item, score: 0 }));
  }

  const results: MatchResult<T>[] = [];
  for (const row of rows) {
    const nameNormalized = row.names.map(normalize).filter(Boolean);
    const nameTokens = row.names.flatMap(tokenize);
    const extraTokens = row.extra.flatMap(tokenize);

    let total = 0;
    let allMatched = true;
    for (const qt of queryTokens) {
      let best = 0;
      // Name-prefix tier: the query token leads the name, either as written
      // ("pull up".startsWith("pull")) or with spaces/hyphens squashed out
      // ("pullup" vs "pull-up" normalized+squashed to "pullup").
      for (const n of nameNormalized) {
        if (n.startsWith(qt) || n.replace(/ /g, '').startsWith(qt)) {
          best = SCORE_NAME_PREFIX;
          break;
        }
      }
      if (best < SCORE_NAME_PREFIX) {
        for (const nt of nameTokens) {
          if (tokenMatches(qt, nt)) {
            best = Math.max(best, SCORE_NAME_WORD);
          }
        }
      }
      if (best === 0) {
        for (const et of extraTokens) {
          if (tokenMatches(qt, et)) {
            best = SCORE_OTHER_FIELD;
            break;
          }
        }
      }
      if (best === 0) {
        allMatched = false;
        break;
      }
      total += best;
    }
    if (allMatched) results.push({ item: row.item, score: total });
  }

  results.sort((a, b) => b.score - a.score);
  return results;
}

/** Convenience: just the matching items, ranked. */
export function search<T>(query: string, rows: readonly Searchable<T>[]): T[] {
  return searchRank(query, rows).map((r) => r.item);
}
